/**
 * DOM-facing helpers for the inline `{{` variable typeahead. Kept separate
 * from `typeahead-reducer.ts` so the decision logic stays pure/testable;
 * these read the live `window.getSelection()` and Range APIs.
 */

/**
 * The character immediately to the left of a collapsed caret, or `null` when
 * unavailable (no selection, expanded selection, or caret sits inside a
 * non-text node such as right after a variable pill/icon).
 */
export const getCharBeforeCaret = (): string | null => {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return null;

  const { focusNode, focusOffset } = sel;
  if (!focusNode || focusNode.nodeType !== Node.TEXT_NODE) return null;

  const text = focusNode.textContent ?? "";
  if (focusOffset > 0) return text[focusOffset - 1] ?? null;

  // Caret at offset 0 of this text node — the preceding char (if any) lives
  // in the previous sibling text node.
  const prev = focusNode.previousSibling;
  if (prev?.nodeType === Node.TEXT_NODE) {
    const prevText = prev.textContent ?? "";
    return prevText[prevText.length - 1] ?? null;
  }
  return null;
};

/**
 * Viewport rect of the current collapsed caret, used to anchor the floating
 * typeahead. Mirrors the fallback chain used elsewhere for caret/pill rects
 * (getClientRects first, getBoundingClientRect as fallback for browsers that
 * return an empty rect list for a collapsed range).
 */
export const getCaretRect = (): DOMRect | null => {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;

  const range = sel.getRangeAt(0).cloneRange();
  range.collapse(true);

  const rects = range.getClientRects();
  if (rects.length > 0) return rects[0];

  const rect = range.getBoundingClientRect();
  if (rect.width || rect.height || rect.x || rect.y) return rect;
  return null;
};

/**
 * Walks backward from a collapsed caret Range by `count` characters, across
 * at most one text-node boundary (the typed `{{query` is expected to live in
 * a single text node — see the reducer's "close on any formatting shortcut"
 * guard, which keeps the buffer from spanning an element boundary).
 *
 * Returns a Range covering the `{{query` span (ready for `deleteContents()`),
 * or `null` when there isn't `count` characters of contiguous text behind the
 * caret — the caller should skip deletion in that case rather than guess.
 */
export const rangeBeforeCaret = (caretRange: Range, count: number): Range | null => {
  if (count <= 0) {
    const r = caretRange.cloneRange();
    r.collapse(true);
    return r;
  }

  const { startContainer, startOffset } = caretRange;
  if (startContainer.nodeType !== Node.TEXT_NODE) return null;

  if (startOffset >= count) {
    const range = new Range();
    range.setStart(startContainer, startOffset - count);
    range.setEnd(startContainer, startOffset);
    return range;
  }

  // Not enough text in this node alone — try exactly one previous sibling
  // text node (e.g. a caret-guard boundary split the text).
  const remaining = count - startOffset;
  const prev = startContainer.previousSibling;
  if (!prev || prev.nodeType !== Node.TEXT_NODE) return null;

  const prevLen = (prev.textContent ?? "").length;
  if (prevLen < remaining) return null;

  const range = new Range();
  range.setStart(prev, prevLen - remaining);
  range.setEnd(startContainer, startOffset);
  return range;
};
